import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import InputOtp from "@/components/ui/InputOtp.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import Switch from "@/sot/primitives/Switch.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import { prettyFormat, splitE164ToCcLocal } from "@/utils/phone.js";

const SECURITY_FIELDS =
  "user_id,sms_2fa_enabled,sms_phone_cc,sms_phone_local,sms_last_confirmed_at,auth_app_2fa_enabled,auth_app_label,auth_app_last_confirmed_at,updated_at";

function normalizePhone(value) {
  const parts = splitE164ToCcLocal(value);
  return parts?.e164 || "";
}

function readVerifiedPhone({ user, profile, profileRow }) {
  const profilePhone = normalizePhone(
    profileRow?.phone || profileRow?.phone_number || profile?.phone || profile?.phone_number || ""
  );
  const authPhone = normalizePhone(user?.phone || "");
  const profileVerifiedAt =
    profileRow?.phone_verified_at ||
    profile?.phone_verified_at ||
    "";
  const authPhoneConfirmedAt =
    user?.phone_confirmed_at ||
    user?.phoneConfirmedAt ||
    "";

  if (profilePhone && profileVerifiedAt) return profilePhone;
  if (authPhone && authPhoneConfirmedAt && (!profilePhone || profilePhone === authPhone)) {
    return authPhone;
  }
  return "";
}

async function loadRoleProfilePhone(userId, role) {
  const table = role === "doctor" ? "doctor_profiles" : "patient_profiles";
  const { data, error } = await supabase
    .from(table)
    .select("phone,phone_verified_at")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

async function loadSecuritySettings(userId) {
  const { data, error } = await supabase
    .from("user_security_settings")
    .select(SECURITY_FIELDS)
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

async function loadTotpFactors() {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  const factors = Array.isArray(data?.all) ? data.all : [];
  return factors.filter((factor) => factor?.factor_type === "totp");
}

async function sendSmsOtp(phone) {
  const { data, error } = await supabase.functions.invoke("send-otp", {
    body: { phone },
  });
  if (error) throw error;
  if (data?.ok === false) {
    const err = new Error(data?.error || "sms_send_failed");
    err.code = data?.error || "sms_send_failed";
    throw err;
  }
  return data || { ok: true };
}

async function verifySmsOtp(phone, code) {
  const { data, error } = await supabase.functions.invoke("verify-otp", {
    body: { phone, code },
  });
  if (error) throw error;
  if (data?.ok !== true) {
    const err = new Error(data?.error || "sms_verify_failed");
    err.code = data?.error || "sms_verify_failed";
    throw err;
  }
  return data;
}

async function saveSmsEnabled(userId, phone) {
  const parts = splitE164ToCcLocal(phone);
  if (!parts?.cc || !parts?.local) {
    const err = new Error("invalid_verified_phone");
    err.code = "invalid_verified_phone";
    throw err;
  }

  const payload = {
    sms_2fa_enabled: true,
    sms_phone_cc: parts.cc,
    sms_phone_local: parts.local,
    sms_last_confirmed_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const update = await supabase
    .from("user_security_settings")
    .update(payload)
    .eq("user_id", userId)
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (!update.error && update.data?.user_id) return update.data;

  const insert = await supabase
    .from("user_security_settings")
    .insert({ user_id: userId, ...payload })
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (insert.error) throw insert.error;
  if (!insert.data?.user_id) {
    const err = new Error("security_settings_save_failed");
    err.code = "security_settings_save_failed";
    throw err;
  }
  return insert.data;
}

function statusCopy(t, settings, verifiedPhone) {
  if (settings?.sms_2fa_enabled) {
    return verifiedPhone
      ? t("settings.twoFactor.sms.enabledForPhone", "SMS codes are enabled for {phone}.", {
          phone: prettyFormat(verifiedPhone),
        })
      : t("settings.twoFactor.sms.enabled", "SMS codes are enabled.");
  }
  if (!verifiedPhone) {
    return t(
      "settings.twoFactor.sms.needsVerifiedPhone",
      "Add and verify your phone number before enabling SMS 2FA."
    );
  }
  return t(
    "settings.twoFactor.sms.support",
    "Use your verified mobile phone to receive a text message with an authentication code for AfriMed™."
  );
}

function authAppStatusCopy(t, { loading, mfaReady, hasVerifiedAuthApp, settings }) {
  if (loading) {
    return t("settings.twoFactor.authApp.loadingStatus", "Loading authenticator app status...");
  }
  if (hasVerifiedAuthApp || settings?.auth_app_2fa_enabled) {
    return t(
      "settings.twoFactor.authApp.enabled",
      "Authenticator app codes are enabled."
    );
  }
  if (!mfaReady) {
    return t(
      "settings.twoFactor.authApp.unavailable",
      "Authenticator app setup is unavailable right now."
    );
  }
  return t(
    "settings.twoFactor.authApp.support",
    "Once enabled, AfriMed™ will use this app to verify your identity during secure access flows."
  );
}

export function SmsTwoFactorHub({ role = "patient", backTo = "/settings/patient/login-security" }) {
  const nav = useNavigate();
  const { t } = useLanguage();
  const { user, profile } = useAuth() || {};
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [settings, setSettings] = React.useState(null);
  const [verifiedPhone, setVerifiedPhone] = React.useState("");
  const [totpFactors, setTotpFactors] = React.useState([]);
  const [mfaReady, setMfaReady] = React.useState(false);
  const [smsSelectionPending, setSmsSelectionPending] = React.useState(false);
  const [authAppSelectionPending, setAuthAppSelectionPending] = React.useState(false);
  const smsSelectionTimerRef = React.useRef(null);
  const authAppSelectionTimerRef = React.useRef(null);

  const setupPath = `/settings/${role}/login-security/two-factor/sms`;
  const authAppSetupPath = `/settings/${role}/login-security/two-factor/auth-app`;
  const smsEnabled = settings?.sms_2fa_enabled === true;
  const hasVerifiedAuthApp = totpFactors.some((factor) => factor?.status === "verified");
  const authAppEnabled = hasVerifiedAuthApp || settings?.auth_app_2fa_enabled === true;
  const authAppAvailable = mfaReady && !hasVerifiedAuthApp;

  const beginSmsSetup = React.useCallback(() => {
    if (loading || smsEnabled || !verifiedPhone || smsSelectionPending) return;
    setSmsSelectionPending(true);
    window.clearTimeout(smsSelectionTimerRef.current);
    smsSelectionTimerRef.current = window.setTimeout(() => {
      nav(setupPath);
    }, 1500);
  }, [loading, nav, setupPath, smsEnabled, smsSelectionPending, verifiedPhone]);

  const beginAuthAppSetup = React.useCallback(() => {
    if (loading || authAppEnabled || !authAppAvailable || authAppSelectionPending) return;
    setAuthAppSelectionPending(true);
    window.clearTimeout(authAppSelectionTimerRef.current);
    authAppSelectionTimerRef.current = window.setTimeout(() => {
      nav(authAppSetupPath);
    }, 1500);
  }, [authAppAvailable, authAppEnabled, authAppSelectionPending, authAppSetupPath, loading, nav]);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        if (!user?.id) {
          setSettings(null);
          setVerifiedPhone("");
          setTotpFactors([]);
          setMfaReady(false);
          return;
        }

        const [profileRow, securityRow, totpRows] = await Promise.all([
          loadRoleProfilePhone(user.id, role),
          loadSecuritySettings(user.id),
          loadTotpFactors(),
        ]);

        if (!mounted) return;
        setSettings(securityRow);
        setTotpFactors(totpRows);
        setMfaReady(true);
        setVerifiedPhone(readVerifiedPhone({ user, profile, profileRow }));
      } catch (err) {
        if (!mounted) return;
        setTotpFactors([]);
        setMfaReady(false);
        setError(
          t(
            "settings.twoFactor.errors.loadSettings",
            "Could not load two-factor settings."
          )
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [profile, role, t, user]);

  React.useEffect(() => {
    return () => {
      window.clearTimeout(smsSelectionTimerRef.current);
      window.clearTimeout(authAppSelectionTimerRef.current);
    };
  }, []);

  return (
    <SettingsScreenShell>
      <TopBar
        title={t("settings.twoFactor.title", "Two-factor authentication")}
        onBack={() => nav(backTo)}
      />

      <div className="mt-5 space-y-4">
        <p className="text-body-medium text-on-surface-var">
          {t(
            "settings.twoFactor.intro",
            "Add an extra layer of security to your AfriMed™ account."
          )}
        </p>

        {error ? (
          <div className="rounded-xl bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
            {error}
          </div>
        ) : null}

        <div className="w-full overflow-hidden rounded-xl bg-[rgb(var(--afrimed-surface,255,255,255))]">
          <div
            role={!smsEnabled && verifiedPhone ? "button" : undefined}
            tabIndex={!smsEnabled && verifiedPhone ? 0 : undefined}
            onClick={beginSmsSetup}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                beginSmsSetup();
              }
            }}
            className={[
              "flex min-h-[76px] items-center gap-3 px-4 py-3 text-body-large text-on-surface",
              !smsEnabled && verifiedPhone ? "cursor-pointer" : "",
            ].join(" ")}
          >
            <div className="min-w-0 flex-1">
              <div>{t("settings.twoFactor.sms.title", "Text message")}</div>
              <div className="mt-1 text-body-medium leading-5 text-on-surface-var">
                {loading
                  ? t("settings.twoFactor.sms.loadingStatus", "Loading SMS status...")
                  : statusCopy(t, settings, verifiedPhone)}
              </div>
            </div>
            <Switch
              checked={smsEnabled || smsSelectionPending}
              disabled={loading || !verifiedPhone || smsEnabled || smsSelectionPending}
              onChange={beginSmsSetup}
            />
          </div>

          <div className="ml-4 h-px bg-[rgba(24,50,119,0.06)]" />

          <div
            role={authAppAvailable && !authAppEnabled ? "button" : undefined}
            tabIndex={authAppAvailable && !authAppEnabled ? 0 : undefined}
            onClick={beginAuthAppSetup}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                beginAuthAppSetup();
              }
            }}
            className={[
              "flex min-h-[76px] items-center gap-3 px-4 py-3 text-body-large text-on-surface",
              authAppAvailable && !authAppEnabled ? "cursor-pointer" : "",
            ].join(" ")}
          >
            <div className="min-w-0 flex-1">
              <div>{t("settings.twoFactor.authApp.title", "Authentication app")}</div>
              <div className="mt-1 text-body-medium leading-5 text-on-surface-var">
                {authAppStatusCopy(t, {
                  loading,
                  mfaReady,
                  hasVerifiedAuthApp,
                  settings,
                })}
              </div>
            </div>
            <Switch
              checked={authAppEnabled || authAppSelectionPending}
              disabled={loading || !authAppAvailable || authAppEnabled || authAppSelectionPending}
              onChange={beginAuthAppSetup}
            />
          </div>
        </div>
      </div>
    </SettingsScreenShell>
  );
}

export function SmsTwoFactorSetup({ role = "patient", doneTo = "/settings/patient/login-security/two-factor" }) {
  const nav = useNavigate();
  const { t } = useLanguage();
  const { user, profile } = useAuth() || {};
  const [loading, setLoading] = React.useState(true);
  const [sending, setSending] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState("");
  const [verifiedPhone, setVerifiedPhone] = React.useState("");
  const [code, setCode] = React.useState("");
  const [sent, setSent] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        if (!user?.id) {
          setVerifiedPhone("");
          return;
        }
        const profileRow = await loadRoleProfilePhone(user.id, role);
        if (!mounted) return;
        setVerifiedPhone(readVerifiedPhone({ user, profile, profileRow }));
      } catch (err) {
        if (!mounted) return;
        setError(
          t(
            "settings.twoFactor.errors.loadPhone",
            "Could not load your verified phone."
          )
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [profile, role, t, user]);

  async function handleSendCode() {
    if (!verifiedPhone || sending) return;
    setSending(true);
    setError("");
    try {
      await sendSmsOtp(verifiedPhone);
      setSent(true);
      setCode("");
    } catch (err) {
      setError(
        t(
          "settings.twoFactor.errors.sendCode",
          "Could not send the SMS code."
        )
      );
    } finally {
      setSending(false);
    }
  }

  async function handleVerifyCode() {
    if (!user?.id || !verifiedPhone || code.length !== 6 || verifying) return;
    setVerifying(true);
    setError("");
    try {
      await verifySmsOtp(verifiedPhone, code);
      await saveSmsEnabled(user.id, verifiedPhone);
      nav(doneTo, { replace: true });
    } catch (err) {
      setError(
        t(
          "settings.twoFactor.errors.verifyCode",
          "We couldn't verify that code."
        )
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="w-full" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(32px+env(safe-area-inset-bottom))] md:pb-24">
        <TopBar
          title={t("settings.twoFactor.sms.setupTitle", "Set up SMS")}
          onBack={() => nav(doneTo)}
        />

        <div className="mx-auto mt-8 flex w-full max-w-[420px] flex-col items-center text-center">
          <h1 className="text-title-large text-on-surface">
            {t(
              "settings.twoFactor.sms.setupHeading",
              "Secure your account with SMS"
            )}
          </h1>
          <p className="mt-2 text-body-large text-on-surface-var">
            {t(
              "settings.twoFactor.sms.setupIntro",
              "We will send a 6-digit code to your verified phone number."
            )}
          </p>

          <Card className="mt-8 w-full px-4 py-4 text-left">
            <div className="text-label-medium text-on-surface-var">
              {t("settings.twoFactor.sms.verifiedPhoneLabel", "Verified phone")}
            </div>
            <div className="mt-1 text-body-large text-on-surface">
              {loading
                ? t("common.loading", "Loading...")
                : verifiedPhone
                  ? prettyFormat(verifiedPhone)
                  : t(
                      "settings.twoFactor.sms.noVerifiedPhone",
                      "No verified phone on file"
                    )}
            </div>
          </Card>

          {!loading && !verifiedPhone ? (
            <div className="mt-6 w-full rounded-xl bg-[var(--state-critical-08)] px-4 py-3 text-left text-body-medium text-[rgb(var(--schemes-critical))]">
              {t(
                "settings.twoFactor.sms.needsVerifiedPhoneSetup",
                "SMS 2FA needs a verified phone number before it can be enabled."
              )}
            </div>
          ) : null}

          {error ? (
            <div className="mt-6 w-full rounded-xl bg-[var(--state-critical-08)] px-4 py-3 text-left text-body-medium text-[rgb(var(--schemes-critical))]">
              {error}
            </div>
          ) : null}

          {!sent ? (
            <div className="mt-8 w-full">
              <Button
                size="cta"
                fullWidth
                loading={sending}
                disabled={loading || !verifiedPhone}
                onClick={handleSendCode}
              >
                {t("settings.twoFactor.sms.sendCode", "Send code")}
              </Button>
            </div>
          ) : (
            <div className="mt-8 w-full">
              <div className="mb-4 text-left text-title-medium text-on-surface-var">
                {t("settings.twoFactor.sms.codeLabel", "6-digit code")}
              </div>
              <InputOtp
                value={code}
                onChange={setCode}
                length={6}
                align="start"
                tone="surface"
                autoFocus
              />
              <p className="mt-5 text-body-large text-on-surface-var">
                {t(
                  "settings.twoFactor.sms.enterCodePrefix",
                  "Enter the code sent to"
                )}{" "}
                <span className="text-on-surface">{prettyFormat(verifiedPhone)}</span>.
              </p>
              <div className="mt-8 space-y-3">
                <Button
                  size="cta"
                  fullWidth
                  loading={verifying}
                  disabled={code.length !== 6}
                  onClick={handleVerifyCode}
                >
                  {t("settings.twoFactor.sms.verifyAndEnable", "Verify and enable")}
                </Button>
                <Button
                  size="cta"
                  variant="ghost"
                  fullWidth
                  disabled={sending || verifying}
                  onClick={handleSendCode}
                >
                  {t("settings.twoFactor.sms.resendCode", "Resend code")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
